import React, { useCallback } from "react";
import { useTranslation } from "locales";
import {
    Form,
    InputControl,
    Picker,
    Button,
    Text,
    TextInput,
    NumberInput,
    StyleFunction,
    useThemedStyle,
    Spacer,
    InputLabel,
} from "@venuepos/react-common";
import { ILayoutCreate } from "lib";
import { View } from "react-native";
import { defaultLayout } from "./forms";

export function LayoutCreateForm(props: {
    form: Form<ILayoutCreate>;
    onSubmit: () => void;
    submitButton: [string, string];
    layoutSizes: any[];
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, setValues, handleSubmit }] =
        props.form;

    const handleColumnCountChange = useCallback(
        (value: number) =>
            setValues({
                columns: value!,
                gridLabel: "custom",
            }),
        [setValues]
    );

    const handleRowCountChange = useCallback(
        (value: number) =>
            setValues({
                rows: value!,
                gridLabel: "custom",
            }),
        [setValues]
    );

    const handleLayoutChange = useCallback(
        (value: (typeof defaultLayout)["gridLabel"]) => {
            let newColumnCount = values?.columns;
            let newRowCount = values?.rows;

            if (props.layoutSizes[Number(value)] !== undefined) {
                const selectedLayoutSize = props.layoutSizes[Number(value)];
                newColumnCount = selectedLayoutSize.columns;
                newRowCount = selectedLayoutSize.rows;
            }

            setValues({
                gridLabel: value,
                columns: newColumnCount,
                rows: newRowCount,
            });
        },
        [props.layoutSizes, setValues, values?.columns, values?.rows]
    );

    if (!values) {
        return null;
    }

    return (
        <>
            <InputControl error={errors.name}>
                <TextInput
                    label={t("common.name", "Name")}
                    placeholder={t(
                        "backoffice.common.enter_name",
                        "Enter name"
                    )}
                    value={values.name}
                    onChangeText={text => setValue("name", text)}
                    testID="layout:name"
                    required={true}
                />
            </InputControl>

            <InputControl>
                <Picker
                    label={t(
                        "backoffice.layout_form.size",
                        "Select a layout format"
                    )}
                    onValueChange={handleLayoutChange}
                    testID="layout:dimensions"
                    selectedValue={values.gridLabel}
                >
                    <Picker.Item
                        label={t(
                            "backoffice.layout_form.pick_format",
                            "Pick a format"
                        )}
                        value="none"
                    />
                    {props.layoutSizes.map((layout, index) => (
                        <Picker.Item
                            label={layout.label}
                            value={index.toString()}
                            key={`layout_${index}`}
                            testID={`layout:dimensions:${layout.label}`}
                        />
                    ))}
                    <Picker.Item
                        label={t(
                            "backoffice.layout_form.custom_format",
                            "Custom"
                        )}
                        value="custom"
                    />
                </Picker>
            </InputControl>

            <InputControl error={errors.columns || errors.rows}>
                <InputLabel>{t("backoffice.common.size", "Size")}</InputLabel>
                <View style={styles.rowContainer}>
                    <View style={styles.column}>
                        <NumberInput
                            value={values.columns}
                            unit={t(
                                "backoffice.layout_form.columns",
                                "Columns"
                            )}
                            decimals={0}
                            max={10}
                            min={1}
                            onChange={handleColumnCountChange}
                            testID="layout:columns"
                        />
                    </View>
                    <Spacer />
                    <View>
                        <Text>×</Text>
                    </View>
                    <Spacer />
                    <View style={styles.column}>
                        <NumberInput
                            value={values.rows}
                            unit={t("backoffice.layout_form.rows", "Rows")}
                            decimals={0}
                            max={10}
                            min={1}
                            onChange={handleRowCountChange}
                            testID="layout:rows"
                        />
                    </View>
                </View>
            </InputControl>

            <Button onPress={handleSubmit(props.onSubmit)} testID="layout:save">
                {t(props.submitButton[0], props.submitButton[1])}
            </Button>
        </>
    );
}

const styleFunc: StyleFunction = () => ({
    rowContainer: {
        flexDirection: "row",
        flex: 1,
        alignItems: "baseline",
    },
    column: { flex: 1 },
});
